<script lang="ts" setup>
import {type UseOffsetPaginationReturn, useWindowScroll} from "@vueuse/core";
import {PhArrowLeft, PhArrowRight} from "@phosphor-icons/vue";
import {NuxtLinkLocale} from "#components";
import {withQuery} from "ufo";

const {
  pagination,
  enablePages = false,
  enableLinks = false,
} = defineProps<{
  pagination: UseOffsetPaginationReturn;
  total: number;
  enablePages?: boolean;
  enableLinks?: boolean;
}>();
const route = useRoute();

const {isFirstPage, prev, currentPage, pageCount, isLastPage, next} =
  pagination;

const resolveElementType = (disabled: boolean) => {
  if (enableLinks) {
    return disabled ? "div" : NuxtLinkLocale;
  }
  return "button";
};

const getLink = (page: number) => {
  if (!enableLinks) {
    return null;
  }
  return withQuery(route.fullPath, {
    page: page === 1 ? undefined : page,
  });
};
</script>

<template>
  <nav
    v-if="total > 0"
    class="pagination col-span-full flex flex-col bg-black p-24"
    role="navigation"
    :aria-label="$t('label.pagination.label')">
    <div class="controls flex flex-row justify-between mx-4 lg:mx-40 2xl:mx-80">
      <component
        :is="resolveElementType(isFirstPage)"
        :disabled="isFirstPage ? true : null"
        :aria-disabled="isFirstPage ? true : null"
        class="flex items-center"
        :class="
          isFirstPage
            ? 'text-white cursor-not-allowed'
            : 'cursor-pointer text-sunrise-100'
        "
        @click="prev"
        :to="getLink(currentPage - 1)"
        :aria-label="$t('label.pagination.goToPreviousPage')">
        <PhArrowLeft class="text-2xl mr-1" />
        <span class="text-xs font-sans">\\</span>
      </component>

      <div class="pages text-white" v-if="enablePages">
        <component
          :is="resolveElementType(false)"
          v-for="item in pageCount"
          :key="item"
          :class="{'text-sunrise-100 font-bold': item === currentPage}"
          class="text-xl font-sans px-2 py-2"
          @click="
            currentPage = item;
          "
          :aria-current="item === currentPage ? 'location' : null"
          :aria-label="
            item === currentPage
              ? $t('label.pagination.currentPage', [item])
              : $t('label.pagination.goToPage', [item])
          ">
          {{ item }}
        </component>
      </div>

      <component
        :is="resolveElementType(isLastPage)"
        :disabled="isLastPage ? true : null"
        :aria-disabled="isLastPage ? true : null"
        class="flex items-center"
        :class="
          isLastPage
            ? 'text-white cursor-not-allowed'
            : 'cursor-pointer text-sunrise-100'
        "
        @click="next"
        :to="getLink(currentPage + 1)"
        :aria-label="$t('label.pagination.goToNextPage')">
        <span class="text-xs font-sans">//</span>
        <PhArrowRight class="text-2xl ml-1" />
      </component>
    </div>
  </nav>
</template>
